// **React Imports
import { useMemo, useRef, useCallback } from 'react'

// **Components Imports
import { Box, Paper, Typography } from '@mui/material'
import Icon from '../icon'
import RequiredLabelComponent from '../required-label-component'

// **Context Imports
import { useFileDragAndDrop } from '../../hooks/useFileDragAndDrop'

// **Utils Imports
import { validateZeroByteFile } from '../../utils/utils'

const ResumeUpload = ({
  id = 'upload-resume',
  label,
  error = '',
  value = null,
  isRequired = false,
  handleChange = () => {},
  formats = ['.pdf', '.docx', '.doc']
}) => {
  const fileInputRef = useRef(null)

  // handlers
  const handleFileDrop = useCallback(
    (files) => {
      const file = files?.[0]
      if (file) {
        if (validateZeroByteFile(file)) {
          return
        }
        handleChange(file)
      }
    },
    [handleChange]
  )

  // hooks
  const { dragActive, dragProps } = useFileDragAndDrop(handleFileDrop)

  // variables
  const formatsLabel = useMemo(() => {
    return formats?.length > 0 ? `Supported formats: ${formats.join(', ')}` : ''
  }, [formats])

  const acceptString = useMemo(() => {
    return formats?.length > 0 ? formats.join(',') : undefined
  }, [formats])

  const paperStyles = useMemo(
    () => ({
      borderStyle: 'dashed',
      borderWidth: '2px',
      borderColor: dragActive ? 'primary.main' : 'var(--divider)',
      backgroundColor: dragActive ? 'rgba(30, 144, 255, 0.04)' : 'transparent',
      padding: 3,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      transition: 'all 0.2s ease-in-out',
      '&:hover': { borderColor: 'primary.main' }
    }),
    [dragActive]
  )

  const handleFileChange = useCallback(
    (e) => {
      const file = e?.target?.files?.[0]
      if (file) {
        if (validateZeroByteFile(file)) {
          e.target.value = null
          return
        }
        handleChange(file)
      }
      e.target.value = null
    },
    [handleChange]
  )

  const handlePaperClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleCloseClick = useCallback(
    (e) => {
      e.stopPropagation()
      handleChange(null)
    },
    [handleChange]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {label && (
        <RequiredLabelComponent
          label={label}
          required={isRequired}
          error={Boolean(error)}
        />
      )}
      <Paper
        variant="outlined"
        sx={paperStyles}
        onClick={handlePaperClick}
        {...dragProps}
      >
        <Icon
          icon={
            dragActive
              ? 'material-symbols:download-done'
              : 'material-symbols:upload'
          }
          nonHoverBoxStyle={{ width: 'max-content' }}
          style={{ fontSize: '2rem', color: 'var(--primary-main)' }}
        />
        <Typography variant="body1" sx={{ mt: 1 }}>
          {dragActive ? 'Drop file here' : 'Click or drag file to upload'}
        </Typography>
        {formatsLabel && (
          <Typography variant="body2" color="text.secondary">
            {formatsLabel}
          </Typography>
        )}
        <input
          ref={fileInputRef}
          id={`file-input-${id}`}
          type="file"
          hidden
          accept={acceptString}
          onChange={handleFileChange}
        />
        {value && (
          <Box sx={{ gap: '8px', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected: <strong>{value?.name}</strong>
            </Typography>
            <Icon
              hoverEffect
              icon={'carbon:close-outline'}
              onClick={handleCloseClick}
            />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Paper>
    </Box>
  )
}

export default ResumeUpload
